export type AttributeTemplateStatus = "active" | "inactive";

export interface AttributeTemplateOption {
  id: string;
  value: string;
  label: string;
  sortOrder: number;
  imageUrl: string | null;
  priceModifier: number | null;
}

export interface AttributeTemplateAttribute {
  id: string;
  name: string;
  type: string;
  isRequired: boolean;
  sortOrder: number;
  options: AttributeTemplateOption[];
  code: string;
}

export interface AttributeTemplateItem {
  id: string;
  templateId?: string;
  storeId?: string;
  name: string;
  description: string;
  attributeCount: number;
  productCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  attributes: AttributeTemplateAttribute[];
}

export interface AttributeTemplateFormOption {
  id: string;
  label: string;
}

export interface AttributeTemplateFormAttribute {
  id: string;
  name: string;
  isRequired: boolean;
  options: AttributeTemplateFormOption[];
}

export interface AttributeTemplateFormValues {
  name: string;
  description: string;
  status: AttributeTemplateStatus;
  attributes: AttributeTemplateFormAttribute[];
}

export const makeAttributeTemplateId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const EMPTY_ATTRIBUTE_TEMPLATE_ID = "00000000-0000-0000-0000-000000000000";

const resolveStableAttributeTemplateId = (
  rawId: string | undefined,
  fallbackKey: string,
  usedIds: Set<string>,
) => {
  const normalizedId = rawId?.trim();
  const isInvalidId =
    !normalizedId || normalizedId === EMPTY_ATTRIBUTE_TEMPLATE_ID;

  let nextId = isInvalidId ? `${fallbackKey}-${makeAttributeTemplateId()}` : normalizedId;

  if (usedIds.has(nextId)) {
    nextId = `${fallbackKey}-${makeAttributeTemplateId()}`;
  }

  usedIds.add(nextId);

  return nextId;
};

export const makeAttributeTemplateOption = (
  optionIndex: number,
): AttributeTemplateOption => ({
  id: makeAttributeTemplateId(),
  value: `option_${optionIndex}`,
  label: `Option ${optionIndex}`,
  sortOrder: optionIndex,
  imageUrl: null,
  priceModifier: null,
});

export const makeEmptyAttributeTemplateFormOption = (): AttributeTemplateFormOption => ({
  id: makeAttributeTemplateId(),
  label: "",
});

export const makeAttributeTemplateAttribute = (
  attributeIndex: number,
): AttributeTemplateAttribute => ({
  id: makeAttributeTemplateId(),
  name: `New Attribute ${attributeIndex}`,
  type: "select",
  isRequired: false,
  sortOrder: attributeIndex,
  code: `NEW_ATTRIBUTE_${attributeIndex}`,
  options: [makeAttributeTemplateOption(1)],
});

export const makeEmptyAttributeTemplateFormAttribute = (): AttributeTemplateFormAttribute => ({
  id: makeAttributeTemplateId(),
  name: "",
  isRequired: false,
  options: [makeEmptyAttributeTemplateFormOption()],
});

export const normalizeAttributeTemplate = (
  item: AttributeTemplateItem,
): AttributeTemplateItem => {
  const attributeIds = new Set<string>();

  return {
    ...item,
    id: resolveStableAttributeTemplateId(
      item.id,
      "template",
      new Set<string>(),
    ),
    isActive: item.isActive ?? true,
    createdAt: item.createdAt || new Date().toISOString(),
    updatedAt: item.updatedAt || item.createdAt || new Date().toISOString(),
    productCount: item.productCount ?? 0,
    attributes: item.attributes.map((attribute, attributeIndex) => {
      const optionIds = new Set<string>();

      return {
        ...attribute,
        id: resolveStableAttributeTemplateId(
          attribute.id,
          `attribute-${attributeIndex + 1}`,
          attributeIds,
        ),
        sortOrder: attributeIndex + 1,
        options: attribute.options.map((option, optionIndex) => ({
          ...option,
          id: resolveStableAttributeTemplateId(
            option.id,
            `option-${attributeIndex + 1}-${optionIndex + 1}`,
            optionIds,
          ),
          sortOrder: optionIndex + 1,
        })),
      };
    }),
    attributeCount: item.attributeCount ?? item.attributes.length,
  };
};

export const cloneAttributeTemplate = (
  item: AttributeTemplateItem,
): AttributeTemplateItem =>
  normalizeAttributeTemplate({
    ...item,
    attributes: item.attributes.map((attribute) => ({
      ...attribute,
      options: attribute.options.map((option) => ({ ...option })),
    })),
  });

const slugifyValue = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const templateToFormValues = (
  template: AttributeTemplateItem | null,
): AttributeTemplateFormValues => ({
  name: template?.name || "",
  description: template?.description || "",
  status: template ? (template.isActive ? "active" : "inactive") : "active",
  attributes: template
    ? template.attributes.map((attribute) => ({
        id: attribute.id,
        name: attribute.name,
        isRequired: attribute.isRequired,
        options: attribute.options.map((option, optionIndex) => ({
          id: option.id,
          label: option.label || option.value || `Option ${optionIndex + 1}`,
        })),
      }))
    : [],
});

export const formValuesToTemplate = (
  values: AttributeTemplateFormValues,
  existingTemplate?: AttributeTemplateItem | null,
): AttributeTemplateItem => {
  const existing = existingTemplate || null;
  const now = new Date().toISOString();

  return normalizeAttributeTemplate({
    id: existing?.id || makeAttributeTemplateId(),
    templateId: existing?.templateId,
    storeId: existing?.storeId,
    name: values.name.trim(),
    description: values.description.trim(),
    attributeCount: values.attributes.length,
    productCount: existing?.productCount ?? 0,
    isActive: values.status === "active",
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    attributes: values.attributes.map((attribute, attributeIndex) => ({
      id: attribute.id || makeAttributeTemplateId(),
      name: attribute.name.trim(),
      type: "select",
      isRequired: attribute.isRequired,
      sortOrder: attributeIndex + 1,
      code:
        attribute.name
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "") ||
        `ATTRIBUTE_${attributeIndex + 1}`,
      options: attribute.options.map((option, optionIndex) => ({
        id: option.id || makeAttributeTemplateId(),
        value: slugifyValue(option.label) || `option_${optionIndex + 1}`,
        label: option.label.trim(),
        sortOrder: optionIndex + 1,
        imageUrl: null,
        priceModifier: null,
      })),
    })),
  });
};
